/* =========================================================================== *\
 __      __.__     ___________           .__    ____ ________  ________.________
/  \    /  \__| ___\__    ___/___   ____ |  |__/_   /   __   \/   __   \   ____/
\   \/\/   /  |/    \|    |_/ __ \_/ ___\|  |  \|   \____    /\____    /____  \ 
 \        /|  |   |  \    |\  ___/\  \___|   Y  \   |  /    /    /    //       \
  \__/\  / |__|___|  /____| \___  >\___  >___|  /___| /____/    /____//______  /
       \/          \/           \/     \/     \/                             \/ 
\* =========================================================================== */

import React, { useEffect, useState, useRef } from 'react';
import { useParams } from 'react-router-dom';
import PropTypes from 'prop-types';

// @mui material components
import Icon from '@mui/material/Icon';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableRow from '@mui/material/TableRow';
import Grid from '@mui/material/Grid';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import IconButton from '@mui/material/IconButton';
import Avatar from '@mui/material/Avatar';
import InputAdornment from '@mui/material/InputAdornment';
import TableHead from '@mui/material/TableHead';
import TableContainer from '@mui/material/TableContainer';

// Material Dashboard 2 PRO React components
import MDBox from 'components/MDBox';
import MDButton from 'components/MDButton';
import MDSnackbar from 'components/MDSnackbar';
import MDInput from 'components/MDInput';

// Material Dashboard 2 PRO React examples
import { useTranslation } from 'react-i18next';
import { APP_STATE } from 'services/Define';
import { FetchData } from 'services/Request';
import { useSelector } from 'react-redux';
import * as Func from 'services/Function';
import FormQuantity from 'components/Form/FormQuantity';
import SearchProduct from 'layouts/purchaserequest/SearchProduct';

const initParent = {
  id: '',
  code: '',
  remark: '',
  type_id: '',
  supplier_id: '',
  status: 'edit',
  total: '',
  created_at: '',
  updated_at: '',
};

const initFind = {
  search: '',
  order_by: 'tb.id',
  order_sort: 'asc',
  page: 1,
  perPage: 10,
  pageTotal: 0,
};

function ExportMoveList() {
  const mod = 'export_move_list';
  const app = useSelector((state) => state[APP_STATE]);
  const { t, i18n } = useTranslation();
  const { id } = useParams();
  const [record, setRecord] = useState([]);
  const [find, setFind] = useState(initFind);
  const [notify, setNotify] = useState(null);
  const [loading, setLoading] = useState(true);
  const [modalProduct, setModalProduct] = useState(false);
  const [cboProduct, setCboProduct] = useState([]);
  const [search, setSearch] = useState('');
  const [parent, setParent] = useState(initParent);

  const txtSearchProduct = useRef(null);

  const showNotify = (msg, color) => {
    setNotify(
      <MDSnackbar
        color={color}
        icon='notifications'
        title={t(`app.name`)}
        content={msg}
        dateTime={Func.dateTimeDisplay()}
        open={Boolean(true)}
        close={() => setNotify(!notify)}
      />
    );

    setTimeout(function () {
      setNotify(null);
    }, 3000);
  };

  const view = (data) => {
    setFind(data);
    setLoading(true);

    FetchData('POST', `/${mod}`, app.token, {
      lang: app.lang,
      search: data.search,
      page: data.page,
      per_page: data.perPage,
      orderby: data.order_by,
      ordersort: data.order_sort,
      ref_id: id,
    }).then((result) => {
      if (result.status === 'ok') {
        setRecord(result.data);
        setParent(result.parent);
      }
      setLoading(false);
    });
  };

  const add = (row) => {
    setLoading(true);

    FetchData('PUT', `/${mod}`, app.token, {
      export_id: id,
      barcode_id: row.barcode_id,
      quantity: row.quantity,
      quantity_balance: row.quantity,
      unit_id: row.unit_id,
    }).then((result) => {
      setLoading(false);

      if (result.status === 'ok') {
        view(find);
        showNotify(t(`alert.addcomplete`), 'success');
      } else {
        showNotify(t(`alert.saveerror`), 'error');
      }
    });
  };

  const searchProduct = () => {
    setCboProduct([]);

    FetchData('OPTIONS', `/${mod}/product`, app.token, {
      search: txtSearchProduct.current.value,
    }).then((result) => {
      if (result.status === 'ok') {
        if (result.data.length === 1) {
          add(result.data[0]);
          setSearch('');
        }

        setCboProduct(result.data);

        setTimeout(function () {
          txtSearchProduct.current.focus();
        }, 500);
      } else {
        Func.exitApp();
      }
    });
  };

  const edit = (item) => {
    FetchData('PATCH', `/${mod}/${item.id}`, app.token, {
      quantity: item.quantity,
      quantity_balance: item.quantity,
    }).then((result) => {
      if (result.status === 'ok') {
        view(find);
        showNotify(t(`alert.editcomplete`), 'success');
      } else {
        showNotify(t(`alert.saveerror`), 'error');
      }
    });
  };

  const del = (item) => {
    setLoading(true);

    FetchData('DELETE', `/${mod}/${item.id}`, app.token, {}).then((result) => {
      setLoading(false);

      if (result.status === 'ok') {
        view(find);
        showNotify(t(`alert.deletecomplete`), 'success');
      } else {
        showNotify(t(`alert.deleteerror`), 'error');
      }
    });
  };

  const init = () => {
    FetchData('GET', `/${mod}`, app.token, {
      export_id: id,
    }).then((result) => {
      if (result.status === 'ok') {
        view(find);
      } else {
        Func.exitApp();
      }
    });
  };

  const imageDisplay = (item) => {
    return <Avatar alt={item.name} src={item.image} variant='rounded' />;
  };

  const btnDel = (item) => {
    if (parent.status !== 'edit') return null;

    return (
      <MDButton variant='text' color='error' size='small' iconOnly onClick={() => del(item)}>
        <Icon>delete</Icon>
      </MDButton>
    );
  };

  const renderHeaderEdit = () => {
    return (
      <TableHead>
        <TableRow>
          <TableCell align='center' width={10}>
            No.
          </TableCell>
          <TableCell align='center' width={40}>
            รูป
          </TableCell>
          <TableCell>สินค้า</TableCell>
          <TableCell align='right' width={150}>
            จำนวน
          </TableCell>
          <TableCell>หน่วย</TableCell>
          <TableCell width={10}>&nbsp;</TableCell>
        </TableRow>
      </TableHead>
    );
  };

  const renderRecordEdit = () => {
    return (
      <TableBody>
        {record.map((row) => (
          <TableRow key={row.id}>
            <TableCell align='center'>{row.no}</TableCell>
            <TableCell align='center'>{Func.imageDisplay(row)}</TableCell>
            <TableCell>
              <MDBox>{row.barcode}</MDBox>
              <MDBox>{row.name}</MDBox>
            </TableCell>
            <TableCell align='right'>
              <FormQuantity parent={row} edit={edit} />
            </TableCell>
            <TableCell align='left'>{row.unit_name}</TableCell>
            <TableCell align='center'>{btnDel(row)}</TableCell>
          </TableRow>
        ))}
      </TableBody>
    );
  };

  const renderHeader = () => {
    return (
      <TableHead>
        <TableRow>
          <TableCell align='center' rowSpan={2} width={10}>
            No.
          </TableCell>
          <TableCell rowSpan={2} align='center' width={40}>
            รูป
          </TableCell>
          <TableCell rowSpan={2}>สินค้า</TableCell>
          <TableCell align='center' colSpan={3}>
            ปริมาณ
          </TableCell>
          <TableCell rowSpan={2} width={90}>
            หน่วย
          </TableCell>
        </TableRow>
        <TableRow>
          <TableCell align='right' width={90}>
            ขอโอน
          </TableCell>
          <TableCell align='right' width={90}>
            รับโอน
          </TableCell>
          <TableCell align='right' width={90}>
            ค้างโอน
          </TableCell>
        </TableRow>
      </TableHead>
    );
  };

  const renderRecord = () => {
    return (
      <TableBody>
        {record.map((row) => (
          <TableRow key={row.id}>
            <TableCell align='center'>{row.no}</TableCell>
            <TableCell align='center'>{Func.imageDisplay(row)}</TableCell>
            <TableCell>
              <MDBox>{row.barcode}</MDBox>
              <MDBox>{row.name}</MDBox>
            </TableCell>
            <TableCell align='right' className='color-primary'>
              {Func.numberDisplay(row.quantity)}
            </TableCell>
            <TableCell align='right' className='color-success'>
              {Func.numberDisplay(row.quantity_receive)}
            </TableCell>
            <TableCell align='right' className='color-danger'>
              {Func.numberDisplay(row.quantity_balance)}
            </TableCell>
            <TableCell align='left'>{row.unit_name}</TableCell>
          </TableRow>
        ))}
      </TableBody>
    );
  };

  const renderList = () => {
    return (
      <MDBox>
        <Grid container spacing={2}>
          {parent.status !== 'edit' ? null : (
            <Grid item xs={12} sx={{ textAlign: 'right' }}>
              <MDBox p={2}>
                <SearchProduct add={add} />
              </MDBox>
            </Grid>
          )}
          <Grid item xs={12}>
            <TableContainer sx={{ boxShadow: 'none' }}>
              <Table aria-label='simple table'>
                {parent.status === 'edit' ? renderHeaderEdit() : renderHeader()}
                {parent.status === 'edit' ? renderRecordEdit() : renderRecord()}
              </Table>
            </TableContainer>
          </Grid>
        </Grid>
      </MDBox>
    );
  };

  const renderProduct = () => {
    return (
      <Dialog
        open={modalProduct}
        maxWidth='md'
        keepMounted
        aria-describedby='dialog-product'
        onClose={() => setModalProduct(false)}
        fullWidth
      >
        <DialogTitle>
          {t(`element.add`)}
          <IconButton
            aria-label='close'
            onClick={() => setModalProduct(false)}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <Icon>close</Icon>
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ minHeight: 700 }}>
          <MDBox>
            <MDBox
              noValidate
              component='form'
              role='form'
              onSubmit={(e) => {
                e.preventDefault();
                searchProduct();
              }}
            >
              <MDInput
                name='searchform'
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                fullWidth
                InputProps={{
                  inputRef: txtSearchProduct,
                  startAdornment: (
                    <InputAdornment position='start'>
                      <span>ค้นหาสินค้า :</span>
                    </InputAdornment>
                  ),
                  autoComplete: 'off',
                }}
              />
            </MDBox>
          </MDBox>
          <MDBox>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Table aria-label='product table' size='small'>
                  <TableHead>
                    <TableRow>
                      <TableCell align='center'>รูป</TableCell>
                      <TableCell>SKU</TableCell>
                      <TableCell>สินค้า</TableCell>
                      <TableCell>หน่วย</TableCell>
                      <TableCell>&nbsp;</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {cboProduct.map((row) => (
                      <TableRow
                        hover
                        key={row.id}
                        onClick={() => add(row)}
                        sx={{ cursor: 'pointer' }}
                      >
                        <TableCell width='40'>{imageDisplay(row)}</TableCell>
                        <TableCell>{row.sku}</TableCell>
                        <TableCell>{row.name}</TableCell>
                        <TableCell>{row.unit_name}</TableCell>
                        <TableCell>
                          <Icon>chevron_right</Icon>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Grid>
            </Grid>
          </MDBox>
        </DialogContent>
      </Dialog>
    );
  };

  useEffect(() => {
    i18n.changeLanguage(app.lang);

    init();
  }, [id]);

  return (
    <div>
      {renderList()}
      {renderProduct()}
      {notify}
    </div>
  );
}

export default ExportMoveList;
